import React from 'react';

interface HeroBannerProps {
  image: string;
  minHeight?: string | number;
  padding?: string;
  children: React.ReactNode;
}

/**
 * Hero banner that shows the full, uncropped photo (object-fit: contain)
 * on top of a blurred, darkened version of the same photo used as a
 * full-bleed backdrop. This avoids the "zoomed in" look that happens when
 * a portrait/square photo is stretched with `background-size: cover` inside
 * a wide, short banner.
 */
const HeroBanner: React.FC<HeroBannerProps> = ({ image, minHeight, padding, children }) => {
  return (
    <div
      className="hero-wrap hero-bread"
      style={{ position: 'relative', overflow: 'hidden', zIndex: 1, minHeight, padding }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '-24px',
          backgroundImage: `url('${image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(20px) brightness(0.55)',
          transform: 'scale(1.15)',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(20,30,10,0.55) 0%, rgba(20,30,10,0.3) 100%)',
        }}
      />
      <img
        src={image}
        alt=""
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '85%',
          maxWidth: '80%',
          objectFit: 'contain',
          borderRadius: '10px',
          boxShadow: '0 20px 45px rgba(0, 0, 0, 0.35)',
        }}
      />
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        {children}
      </div>
    </div>
  );
};

export default HeroBanner;
